<template>
  <v-dialog
    v-model="dialog"
    width="680px"
    style="z-index: 9999;"
  >
    <v-card>
      <v-toolbar dense elevation="0">
        <v-toolbar-title>{{ $t("dialog.service.title") }}</v-toolbar-title>
        <div class="flex-grow-1"></div>
        <v-btn @click="store" elevation="0" color="primary">
          {{ $t("dialog.service.store") }}
        </v-btn>
        <v-btn icon @click="dialog=false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider class="mx-2" />

      <v-container>
        <validation-observer ref="form_validation">
          <form @submit.prevent="submit">
            <v-row class="px-2">
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.service.plan"
                  rules=""
                >
                  <v-select
                    v-model="planId"
                    :items="getPlans"
                    :label="$t('dialog.service.plan')"
                    :error-messages="errors"
                    item-value="servicePlanId"
                    clearable
                    :disabled="service !== null && service.planId !== null"
                  >
                    <template v-slot:selection="data">
                      {{ formatPlanName(data.item) }}
                    </template>
                    <template v-slot:item="data">
                      {{ formatPlanName(data.item) }}
                    </template>
                  </v-select>
                </validation-provider>
              </v-col>
            </v-row>

            <v-row class="px-2">
              <v-col cols="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.service.type_code"
                  rules=""
                >
                  <v-select
                    v-model="typeCode"
                    :items="getServiceTypes"
                    :label="$t('dialog.service.type_code')"
                    :error-messages="errors"
                    clearable
                    :disabled="planId !== null"
                  >
                    <template v-slot:selection="data">
                      {{ $t("enum.service_type." + data.item) }}
                    </template>
                    <template v-slot:item="data">
                      {{ $t("enum.service_type." + data.item) }}
                    </template>
                  </v-select>
                </validation-provider>
              </v-col>

              <v-col cols="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.service.name"
                  rules="max:50"
                >
                  <v-text-field
                    v-model="name"
                    :counter="50"
                    :error-messages="errors"
                    :label="$t('dialog.service.name')"
                    :disabled="planId !== null"
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>

            <v-row class="px-2">
              <v-col cols="6">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDate(date)"
                      :label="$t('dialog.service.date')"
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      @click:clear="date = null"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="date"
                    @input="dateMenu = false"
                    no-title
                    scrollable
                    :first-day-of-week="1"
                    :locale="getIsoLocale()"
                  >
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-row class="px-2">
              <v-col cols="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.service.km"
                  rules="decimal:2"
                >
                  <v-text-field
                    v-model="km"
                    :error-messages="errors"
                    :label="$t('dialog.service.km')"
                    clearable
                  ></v-text-field>
                </validation-provider>
              </v-col>

              <v-col cols="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.service.eh"
                  rules="decimal:2"
                >
                  <v-text-field
                    v-model="eh"
                    :error-messages="errors"
                    :label="$t('dialog.service.eh')"
                    clearable
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>

            <v-row class="px-2">
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.service.note"
                  rules=""
                >
                  <v-text-field
                    v-model="description"
                    :error-messages="errors"
                    :label="$t('dialog.service.note')"
                    clearable
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
          </form>
        </validation-observer>
      </v-container>
    </v-card>

    <v-snackbar
      v-model="showError"
      bottom
      light
      timeout="-1"
      color="red lighten-4"
    >
      {{ errorMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="indigo"
          text
          v-bind="attrs"
          @click="showError = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>

import { DateTime } from "luxon";
import utils from "@/utils";
import i18n from "@/plugins/i18n";

export default {
  name: "ServiceDialog",

  data() {
    return {
      dialog: false,
      service: null,

      dateMenu: false,

      planId: null,
      typeCode: null,
      name: null,
      date: null,
      km: null,
      eh: null,
      repeatedPeriod: null,
      repeatedKm: null,
      repeatedEh: null,
      description: null,

      showError: false,
      errorMessage: null
    };
  },

  methods: {
    open(service) {
      if (service === null || service.serviceId === undefined || service.serviceId === null) {
        this.service = null;
        if (service === null)
          service = {}

        this.planId = service.planId || null;
        this.typeCode = service.typeCode || null;
        this.name = service.name || null;
        this.date = service.date || null;
        this.km = null;
        this.eh = null;
        this.description = service.description || null;

      } else {
        this.$store.dispatch("service/getService", service.serviceId).then(response => {
          const s = response;
          this.service = s;

          this.planId = s.planId;
          this.typeCode = s.typeCode;
          this.name = s.name;
          this.date = s.date || null;
          this.km = (utils.isValue(s.km)) ? Math.trunc(s.km / 1000) : null;
          this.eh = (utils.isValue(s.eh)) ? Math.trunc(s.eh / 3600) : null;
          this.description = s.description || null;
        });
      }
      
      this.dialog = true;
      if (this.$refs.form_validation !== undefined)
        this.$refs.form_validation.reset();
    },

    async store() {
      if (await this.$refs.form_validation.validate()) {
        const service = {
          serviceId: (this.service !== null) ? this.service.serviceId : null,
          planId: this.planId,
          name: this.name,
          vehicleId: this.$store.state.service.vehicle.vehicleId,
          typeCode: this.typeCode,
          date: this.date,
          km: (utils.isNumber(this.km)) ? utils.toNumber(this.km) * 1000 : 0,
          eh: (utils.isNumber(this.eh)) ? utils.toNumber(this.eh) * 3600 : 0,
          description: this.description,
          lastChange: (this.service !== null) ? this.service.lastChange : null
        };

        const response = await this.$store.dispatch("service/storeService", { service: service });

        if (response.error) {
          this.showError = true;
          this.errorMessage = response.message;
        } else {
          this.dialog = false;
        }
      }
    },

    getIsoLocale() {
      return i18n.messages[i18n.locale].lang.iso;
    },

    formatDate(day) {
      return (day !== null) ? DateTime.fromISO(day).toLocaleString(DateTime.DATE_SHORT) : "";
    },

    formatPlanName(plan) {
      if (plan.typeCode === "own")
        return plan.name;
      else if (plan.name !== null)
        return this.$t("enum.service_type." + plan.typeCode) + " - " + plan.name;
      else
        return this.$t("enum.service_type." + plan.typeCode);
    }
  },

  computed: {
    getServiceTypes() {
      return this.$store.state.data.listServiceTypes;
    },

    getPlans() {
      return this.$store.state.service.plans;
    },
  },

  watch: {
    dialog(newValue) {
      if (newValue === false)
        this.$store.dispatch("dialogs/hideDialog", "vehicleUpdate");
    }
  },

  mounted() {
    this.$store.dispatch("data/fetchCodes", "serviceTypes");
  }
};
</script>